import type { Lang } from '@/hooks/useLang'

interface BentoFeaturesProps {
  lang: Lang
}

const COPY = {
  fr: {
    tagline: 'Capacités du système',
    main: {
      title: 'Intelligence Symbiotique',
      body: 'Nos modèles intègrent des réseaux neuronaux de type 4, permettant une adaptation temps réel à votre environnement et à vos flux de travail.',
      tag1: 'Sync v4',
      tag2: 'Sync-X',
    },
    security: {
      title: 'Protocole de Sécurité',
      body: "Cryptage biométrique de bout en bout. Votre robot n'obéit qu'à vous, protégé par le standard FRIC-Guard.",
    },
    modular: {
      title: 'Modularité Totale',
      body: "Remplacez n'importe quel composant en quelques secondes.",
    },
    autonomy: {
      title: 'Autonomie Étendue',
      body: "Jusqu'à 72 heures d'opération continue par cycle de charge.",
    },
    support: {
      title: 'Assistance 24/7',
      body: 'Support technique prioritaire intégré à votre configuration.',
    },
  },
  en: {
    tagline: 'System capabilities',
    main: {
      title: 'Symbiotic Intelligence',
      body: 'Our models integrate type-4 neural networks, enabling real-time adaptation to your environment and workflows.',
      tag1: 'Sync v4',
      tag2: 'Sync-X',
    },
    security: {
      title: 'Security Protocol',
      body: 'End-to-end biometric encryption. Your robot responds only to you, protected by the FRIC-Guard standard.',
    },
    modular: {
      title: 'Total Modularity',
      body: 'Replace any component in seconds.',
    },
    autonomy: {
      title: 'Extended Autonomy',
      body: 'Up to 72 hours of continuous operation per charge cycle.',
    },
    support: {
      title: '24/7 Assistance',
      body: 'Priority technical support built into your configuration.',
    },
  },
}

export default function BentoFeatures({ lang }: BentoFeaturesProps) {
  const t = COPY[lang]

  return (
    <section id="features" className="px-8 md:px-16 py-24 bg-surface-container-low">
      {/* Section label */}
      <div className="mb-10 flex items-center gap-4">
        <div className="h-px w-8 bg-primary-container" />
        <span className="label-technical">{t.tagline}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

        {/* Grande card — Intelligence Symbiotique */}
        <div className="md:col-span-8 glass-panel p-10 rounded-xl flex flex-col justify-between min-h-[400px]">
          <div>
            <h3 className="font-headline text-3xl mb-4 text-primary">{t.main.title}</h3>
            <p className="text-on-surface-variant max-w-md font-body leading-relaxed">{t.main.body}</p>
          </div>
          <div className="flex gap-4 mt-8">
            <span className="bg-surface-container px-4 py-2 rounded border border-outline-variant/10 label-technical text-secondary">
              {t.main.tag1}
            </span>
            <span className="bg-surface-container px-4 py-2 rounded border border-outline-variant/10 label-technical text-secondary">
              {t.main.tag2}
            </span>
          </div>
        </div>

        {/* Card verticale — Sécurité */}
        <div className="md:col-span-4 bg-surface-container p-10 rounded-xl border border-outline-variant/5 flex flex-col">
          <span className="material-symbols-outlined text-secondary text-4xl mb-6">security</span>
          <h3 className="font-headline text-2xl mb-4 text-on-surface">{t.security.title}</h3>
          <p className="text-on-surface-variant text-sm leading-relaxed font-body">{t.security.body}</p>
        </div>

        {/* Petites cards */}
        <div className="md:col-span-4 glass-panel p-8 rounded-xl border border-outline-variant/5">
          <h4 className="font-headline text-lg mb-2 text-primary-container">{t.modular.title}</h4>
          <p className="text-on-surface-variant text-sm font-body">{t.modular.body}</p>
        </div>

        <div className="md:col-span-4 glass-panel p-8 rounded-xl border border-outline-variant/5 relative overflow-hidden group">
          <div className="relative z-10">
            <h4 className="font-headline text-lg mb-2 text-primary-container">{t.autonomy.title}</h4>
            <p className="text-on-surface-variant text-sm font-body">{t.autonomy.body}</p>
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-secondary/10 blur-3xl group-hover:bg-secondary/20 transition-all duration-500 pointer-events-none" />
        </div>

        <div className="md:col-span-4 glass-panel p-8 rounded-xl border border-outline-variant/5">
          <h4 className="font-headline text-lg mb-2 text-primary-container">{t.support.title}</h4>
          <p className="text-on-surface-variant text-sm font-body">{t.support.body}</p>
        </div>

      </div>
    </section>
  )
}
